
import { IPlanificationProfileScheduler } from '@/entity/planification/planification-profile-scheduler';
import { addDays, addMonths, addYears, differenceInCalendarDays } from 'date-fns';
import { Vue, Prop, Component, Watch } from 'vue-property-decorator';
import { Timeline } from 'vue2vis';
import { MgtPerson } from '@microsoft/mgt';
import { IPlanificationPhaseProfileWorkDay } from '@/entity/planification/planification-phase-profile-work-day';
import {
    ViewMode,
    IRetrieveKey,
    TimelineEvent,
    IGroupBo,
    IEmployeeBo,
    INewDayOff,
    dateToString,
    setHeader,
    setLocalTranslation,
    displayHeader,
    timelineOption,
    retrieveValues,
    getUserByIds,
    MgtPersonDic,
    isShiftCheckBox,
    isPhaseCollapse,
    isOnClickPhase,
    isOnClickProfile,
    newEmployeBo,
    setHierarchicalOrder,
    isDeleteCheckBox,
    setClassName,
    workoutByState,
    ERecapCalendar,
    generateKeyByProfile,
    generateWorkoutKeyByProfile,
    fillGroupLabel,
    setEmployeeName
} from './calendar-helper';
import GroupLabel from './group-label.vue';
import {
    ConfigShifted,
    isShiftSelected,
    replaceWorkoutDateByShiftedDate
} from './scheduler-shifting-component/scheduler-shifting-helper';
import {
    ConfigDeleted,
    isDeletedSelected,
    replaceWorkoutDateByDeletedDate
} from './scheduler-deleting-component/scheduler-deleting-helper';
import { ConfigManual, replaceWorkoutDateByManualDate } from './scheduler-manual-component/scheduler-manual-helper';

// Documentation timeline: https://visjs.github.io/vis-timeline/docs/timeline/
// exemple nested group : https://visjs.github.io/vis-timeline/examples/timeline/groups/nestedThreeLevels.html
//
@Component({
    components: {
        Timeline,
        MgtPerson
    }
})
export default class Calendar extends Vue {
    @Prop({ required: true }) currentTabIndex!: ERecapCalendar;
    @Prop({ required: true }) loading!: boolean;
    @Prop({ required: true }) profiles!: IPlanificationProfileScheduler[];
    @Prop({ required: true }) configShifted!: ConfigShifted[];
    @Prop({ required: true }) configDeleted!: ConfigDeleted[];
    @Prop({ required: true }) configManual!: ConfigManual[];
    @Prop({ required: true }) delBtnState!: boolean;
    @Prop({ required: true }) shiftBtnState!: boolean;
    @Prop({ required: true }) viewMode!: { mode: ViewMode; version: number };
    @Prop({ required: true }) currentYear!: number;
    @Prop({ required: true }) userFilter!: string[];
    @Prop({ required: true }) employeeRoleFilter!: number[];
    @Prop({ required: true }) isFullDay!: boolean | null;
    emitIdToUpdate(id: number, index: number): void {
        this.$emit('emitIdToUpdate', { id, index });
    }

    emitWorkoutsState(): void {
        this.$emit('emitWorkoutsState', this.newDayOff);
        this.newDayOff = [];
    }

    emitManualConfig(workoutKey: string) {
        this.$emit('onChangeConfigManual', workoutKey);
    }

    emitShiftPhase(group: string) {
        this.$emit('shiftedPhases', group);
    }

    emitDeletedPhase(group: string) {
        this.$emit('deletedPhase', group);
    }

    mgtUsers: MgtPersonDic = {};
    oldCurentYear: number | null = null;
    selectedDate: string | null = null;
    version: number = 0;
    newDayOff: INewDayOff[] = [];
    selectedItem: TimelineEvent | null = null;
    phasesCollapsed: string[] = [];
    selectedWorkout: IRetrieveKey[] = [];
    tooltipVersion: number = 0;

    get timelineOption() {
        const calculatedDate = this.setRange(this.viewMode.mode, this.selectedDate);
        this.viewMode.version++;

        const options = timelineOption(calculatedDate.start, calculatedDate.end);
        return options;
    }

    get currentProfiles() {
        return this.profiles;
    }

    get allWorkoutProfil() {
        return this.profiles.flatMap((p) => {
            return p.planificationPhaseProfileWorkDays.map((w) => {
                return {
                    contractLineId: p.contractLineId,
                    employeeId: p.employeeId,
                    profileId: p.profileId,
                    date: dateToString(w.date, 'yyyy-MM-dd'),
                    datePartId: w.dayPartTypeId,
                    isDeleted: false
                } as INewDayOff;
            });
        });
    }

    get phaseProfilShifts(): IPlanificationProfileScheduler[] {
        return replaceWorkoutDateByShiftedDate(this.configShifted);
    }

    get phaseProfilDeleted(): IPlanificationProfileScheduler[] {
        return replaceWorkoutDateByDeletedDate(this.configDeleted);
    }

    get phaseProfilManual(): IPlanificationProfileScheduler[] {
        return replaceWorkoutDateByManualDate(this.configManual, this.profiles);
    }

    get employeesByPhase(): IGroupBo[] {
        if (this.currentProfiles && this.currentProfiles.length > 0 && !this.loading) {
            const activeCollapses: number[] = [];
            let phases: { contractLineId: number; type: string; obj: IGroupBo }[] = this.currentProfiles
                .sort((a, b) => a.parentPhaseId - b.parentPhaseId)
                .map((profile: IPlanificationProfileScheduler) => {
                    const id = generateKeyByProfile(profile);
                    const componentProps = {
                        elemId: id,
                        phase: profile,
                        hasCollapse: false,
                        isShowNested: false,
                        phaseTotalRate: this.currentProfiles
                            .filter((x) => x.parentPhaseId === profile.phaseId)
                            .reduce((prev: number, item) => prev + item.totalRate, 0),
                        visible: true,
                        mgtUser: null as { displayName: string } | null,
                        isHideBudget: false,
                        shiftBtnState: this.shiftBtnState,
                        isSelectedShift: isShiftSelected(this.phaseProfilShifts, this.profiles, profile),
                        delBtnState: this.delBtnState,
                        isSelectedDel: isDeletedSelected(this.phaseProfilDeleted, this.profiles, profile)
                    };
                    let type: 'phase' | 'user' | '' = '';
                    // Phase
                    if (profile.parentPhaseId === 0) {
                        type = 'phase';
                        componentProps.hasCollapse = (profile.profileNumber ?? 0) > 0;
                        componentProps.isShowNested = !!this.phasesCollapsed.find((pc) => pc === id);
                        if (componentProps.isShowNested) {
                            activeCollapses.push(profile.contractLineId);
                        }
                        // Utilisateur
                    } else if (
                        (this.userFilter.length === 0 || this.userFilter.includes(profile.employeeId.toLowerCase())) &&
                        (this.employeeRoleFilter.length === 0 ||
                            this.employeeRoleFilter.includes(profile.profileRoleId ?? -1)) &&
                        (profile.employeeId !== '' || profile.profileId > 0)
                    ) {
                        type = 'user';
                        componentProps.visible = !!activeCollapses.find((ac) => ac === profile.contractLineId);
                        if (profile.employee) {
                            profile.employee.employeeDisplayName = setEmployeeName(profile, this.mgtUsers);
                        }
                    }
                    const prevBud = new GroupLabel({ propsData: { ...componentProps } });
                    prevBud.$mount();
                    return {
                        contractLineId: profile.contractLineId,
                        type,
                        obj: fillGroupLabel(
                            type,
                            id,
                            prevBud.$el.outerHTML,
                            componentProps.visible,
                            profile.contractLineLabel
                        ) as IGroupBo
                    };
                });

            if (this.userFilter.length > 0) {
                const phaseIds: number[] = [];
                const users = phases.filter((p) => {
                    const key = retrieveValues(p.obj.id.toLowerCase());
                    if (
                        key &&
                        p.type === 'user' &&
                        (this.userFilter.includes(key.employeeId) || this.employeeRoleFilter.includes(key.profileId))
                    ) {
                        phaseIds.push(key.contractLineId);
                        return true;
                    }
                    return false;
                });
                phases = phases
                    .filter(
                        (p) =>
                            p.type === 'phase' &&
                            phaseIds.includes(retrieveValues(p.obj.id.toLowerCase())?.contractLineId ?? -1)
                    )
                    .concat(users);
            }

            const resultOrdered: IGroupBo[] = setHierarchicalOrder(phases);
            return resultOrdered;
        }
        displayHeader(this.loading);
        return [];
    }

    async mounted() {
        this.oldCurentYear = this.currentYear;
        const scheduler: any = this.$refs.scheduler;
        if (scheduler) {
            setLocalTranslation(scheduler);
        }
        const profiles = this.profiles.filter((p) => p.employeeId?.length > 0).map((p) => p.employeeId);

        await getUserByIds(profiles, this.mgtUsers, false);
    }

    @Watch('employeesByPhase')
    adjustVerticalScroll(newVal: IGroupBo[]) {
        this.tooltipVersion++;
        if (newVal.length > 0) {
            setHeader(true);
            this.setScrollPosition();
        } else {
            setHeader(false);
        }
        this.scrollOnInit();
    }

    get workDaysByEmployee(): IEmployeeBo[] {
        if (this.currentProfiles && this.currentProfiles.length > 0 && !this.loading) {
            const phases: IEmployeeBo[] = this.currentProfiles
                .filter((p) => p.parentPhaseId === 0)
                .map((prof) => {
                    const start = dateToString(prof.startDate, 'yyyy-MM-dd');
                    const end = dateToString(prof.endDate, 'yyyy-MM-dd', 1);
                    return {
                        id: generateKeyByProfile(prof),
                        group: generateKeyByProfile(prof),
                        content: '',
                        start: `${start} 00:00:00`,
                        end: `${end} 00:00:00`,
                        type: 'range',
                        className: 'phase-bar-objective'
                    };
                });

            const employees: IEmployeeBo[] = this.currentProfiles
                .filter((p) => p.employeeId !== '' || p.profileId > 0)
                .flatMap((item, index) => {
                    var dayOffAdded = this.newDayOff
                        .filter(
                            (ndo) =>
                                !ndo.isDeleted &&
                                ndo.contractLineId === item.contractLineId &&
                                ndo.employeeId.toLocaleLowerCase() === item.employeeId.toLowerCase()
                        )
                        .map((m) => {
                            return {
                                id: 0,
                                planificationPhaseProfileId: 0,
                                dayPartTypeId: m.datePartId,
                                date: new Date(m.date)
                            } as IPlanificationPhaseProfileWorkDay;
                        });
                    const profileWorkingDays = workoutByState(
                        this.currentTabIndex,
                        item,
                        this.phaseProfilShifts,
                        this.phaseProfilDeleted,
                        this.phaseProfilManual
                    );

                    var workinDayGroupByDate = profileWorkingDays
                        .concat(dayOffAdded)
                        .reduce((acc: { keyDate: string; items: IPlanificationPhaseProfileWorkDay[] }[], item) => {
                            const key = dateToString(item.date, 'yyyy-MM-dd');
                            const obj = acc.find((a) => a.keyDate === key);
                            if (obj) {
                                obj.items.push(item);
                            } else {
                                acc.push({ keyDate: key, items: [item] });
                            }
                            return acc;
                        }, []);
                    var workDays: IEmployeeBo[] = workinDayGroupByDate.flatMap((p) => {
                        let startDateFormatted: string = '';
                        let endDateFormatted: string = '';
                        let className: string = '';

                        if (p.items.filter((p) => p.dayPartTypeId === 1 || p.dayPartTypeId === 2).length === 2) {
                            return [
                                {
                                    ...newEmployeBo(item),
                                    id: generateWorkoutKeyByProfile('full', item, p.keyDate, 0),
                                    start: `${p.keyDate} 00:00:00`,
                                    end: `${dateToString(p.keyDate, 'yyyy-MM-dd', 1)} 00:00:00`,
                                    className: setClassName('day-off-full', p.items)
                                }
                            ];
                        } else if (p.items.length === 1) {
                            const dartPartTypeId = p.items[0].dayPartTypeId;
                            if (dartPartTypeId === 1) {
                                /* matin */
                                className = setClassName('day-off-morning', p.items);
                                startDateFormatted = `${p.keyDate} 00:00:00`;
                                endDateFormatted = `${dateToString(p.keyDate, 'yyyy-MM-dd', 1)} 00:00:00`;
                            } else if (dartPartTypeId === 2) {
                                /* après-midi */
                                className = setClassName('day-off-afternoon', p.items);
                                startDateFormatted = `${p.keyDate} 00:00:00`;
                                endDateFormatted = `${dateToString(p.keyDate, 'yyyy-MM-dd', 1)} 00:00:00`;
                            }
                            return [
                                {
                                    ...newEmployeBo(item),
                                    id: generateWorkoutKeyByProfile('half', item, p.keyDate, dartPartTypeId),
                                    start: startDateFormatted,
                                    end: endDateFormatted,
                                    className: className
                                }
                            ];
                        }
                        return [];
                    });
                    return workDays;
                });

            return phases.concat(employees);
        }
        return [];
    }

    private searchPhase(obj: IRetrieveKey | null) {
        return this.currentProfiles.find(
            (p) =>
                p.contractLineId === obj?.contractLineId &&
                p.employeeId.toLowerCase() === obj.employeeId.toLowerCase() &&
                p.profileId === obj.profileId &&
                p.indexInPhase === obj.indexInPhase
        );
    }

    private updateShift(props: TimelineEvent) {
        const keys = retrieveValues(props.group);
        const profile = this.searchPhase(keys);
        if (!profile) return;
        if (!keys || keys.indexInPhase !== -1) {
            this.emitShiftPhase(props.group);
            return;
        }
        const childsPhase = this.profiles.filter(
            (p) => p.indexInPhase !== -1 && p.contractLineId === keys.contractLineId
        );
        const allChecked = childsPhase.every((cp) => isShiftSelected(this.phaseProfilShifts, this.profiles, cp));
        childsPhase
            .filter((cp) => allChecked || !isShiftSelected(this.phaseProfilShifts, this.profiles, cp))
            .forEach((cp) => {
                const id = generateKeyByProfile(cp);
                this.emitShiftPhase(id);
            });
    }

    private updateDayOff(selectedKey: string, date: string) {
        if ([0, 6].includes(new Date(date).getDay())) {
            return;
        }
        const findKeyIndex = (key: IRetrieveKey, datePartId: number, isOnlyNewDayOff: boolean) => {
            return (isOnlyNewDayOff ? [] : this.allWorkoutProfil)
                .concat(this.newDayOff)
                .findIndex(
                    (w) =>
                        w.contractLineId === key.contractLineId &&
                        w.employeeId === key.employeeId &&
                        w.profileId === key.profileId &&
                        w.indexInPhase === key.indexInPhase &&
                        w.date === date &&
                        w.datePartId === datePartId
                );
        };
        const key = retrieveValues(selectedKey);
        if (key && key.contractLineId > 0 && (key.employeeId.length > 0 || key.profileId > 0)) {
            var newObj = (datePart: number) => {
                return {
                    contractLineId: key.contractLineId,
                    indexInPhase: key.indexInPhase,
                    employeeId: key.employeeId,
                    profileId: key.profileId,
                    date: date,
                    datePartId: datePart,
                    isDeleted: false
                } as INewDayOff;
            };
            const morning = findKeyIndex(key, 1, false);
            const afternoon = findKeyIndex(key, 2, false);
            if (this.isFullDay) {
                if (morning === -1 && afternoon === -1) {
                    this.newDayOff = [...this.newDayOff, newObj(3)];
                } else if (morning === -1 && afternoon > -1) {
                    this.newDayOff.push(newObj(1));
                } else if (morning > -1 && afternoon === -1) {
                    this.newDayOff.push(newObj(2));
                } else if (morning > -1 && afternoon > -1) {
                    this.newDayOff[morning].isDeleted = true;
                    this.newDayOff[afternoon].isDeleted = true;
                }
            } else {
                if (morning === -1 && afternoon === -1) {
                    this.newDayOff.push(newObj(1));
                } else {
                    // se fait au clique + bouton supprimer
                    if (morning > -1) this.newDayOff[morning].isDeleted = true;
                    if (afternoon > -1) this.newDayOff[afternoon].isDeleted = true;
                }
            }
            this.emitWorkoutsState();
        }
    }

    mouseMove(props: TimelineEvent) {
        this.selectedItem = props;
    }

    onSingleClick(props: TimelineEvent) {
        if (this.selectedItem && this.selectedItem.event.type === 'pointerdown') {
            if (props.event.timeStamp - this.selectedItem.event.timeStamp > 200) return;
        }

        if (!props.time) return;

        var obj = props?.group ? retrieveValues(props.group) : null;
        switch (props.what) {
            case 'group-label': {
                if (isPhaseCollapse(props)) {
                    const pc = this.phasesCollapsed.findIndex((pc) => pc === props.group);
                    if (pc > -1) this.phasesCollapsed.splice(pc, 1);
                    else {
                        this.phasesCollapsed.push(props.group);
                    }
                    break;
                } else if (isShiftCheckBox(props)) {
                    this.updateShift(props);
                } else if (isDeleteCheckBox(props)) {
                    this.emitDeletedPhase(props.group);
                } else if (isOnClickPhase(props)) {
                    const phase = this.searchPhase(obj);
                    if (phase) {
                        const phaseId = phase.phaseId > 0 ? phase.phaseId : phase.parentPhaseId;
                        if (phaseId > 0 && phase.statusCode !== 'BLC') {
                            this.emitIdToUpdate(phaseId, -1);
                        }
                    }
                } else if (isOnClickProfile(props)) {
                    const keys = retrieveValues(props.group);
                    const phase = this.searchPhase(obj);
                    if (phase && keys && keys.indexInPhase! > -1) {
                        const phaseId = phase.phaseId > 0 ? phase.phaseId : phase.parentPhaseId;
                        if (phaseId > 0 && phase.statusCode !== 'BLC') {
                            this.emitIdToUpdate(phaseId, keys.indexInPhase!);
                        }
                    }
                }
                break;
            }
            case 'item': {
                if (this.currentTabIndex === ERecapCalendar.ManualSelection) {
                    this.emitManualConfig(props.item);
                }
                break;
            }
        }
        /* A revoir & non demandé. */
        // const phase = this.searchPhase(obj);
        // if (phase) {
        //     const phaseId = phase.phaseId > 0 ? phase.phaseId : phase.parentPhaseId;
        //     if (phaseId > 0) {
        //         const parsedDate = dateToString(phase.startDate, 'yyyy-MM-dd');
        //         this.selectedDate = parsedDate.length === 10 ? parsedDate : null;
        //         if (this.selectedDate !== null) {
        //             const year = new Date(this.selectedDate).getFullYear();
        //             if (!isNaN(year)) this.$emit('emitUpdateYear', year);
        //         }
        //     }
        // }
        this.selectedItem = props;
    }

    dbClick(props: TimelineEvent) {
        const isProfil = (obj: IRetrieveKey | null) =>
            obj && obj.contractLineId > 0 && (obj.employeeId.length > 0 || obj.profileId > 0);

        var obj = props?.group ? retrieveValues(props.group) : null;
        switch (props.what) {
            case 'item': {
                if (this.currentTabIndex === ERecapCalendar.ManualSelection && isProfil(obj)) {
                    this.updateDayOff(props.group, props.snappedTime.format('YYYY-MM-DD'));
                }
                break;
            }
            case 'background': {
                if (this.currentTabIndex === ERecapCalendar.ManualSelection && isProfil(obj)) {
                    this.updateDayOff(props.group, props.snappedTime.format('YYYY-MM-DD'));
                }
                break;
            }
        }
    }

    scrollOnInit() {
        if (this.version === 1) {
            this.version++;
            window.setTimeout(() => {
                const scrollAfter = document.querySelector('.vis-vertical-scroll');
                if (scrollAfter) {
                    scrollAfter.scrollTop = 0;
                }
            }, 1);
        }
    }

    phaseAxis: {
        element: HTMLElement | null;
        currentScroll: number;
        oldHeight: number;
    } = {
        element: null,
        currentScroll: 0,
        oldHeight: 0
    };

    setScrollPosition() {
        if (!this.phaseAxis.element) {
            this.phaseAxis.element = document.querySelector('.vis-left.vis-vertical-scroll') as HTMLElement;
        }
        if (this.phaseAxis.element) {
            this.phaseAxis.currentScroll = this.phaseAxis.element?.scrollTop ?? 0;
            window.setTimeout(() => {
                this.phaseAxis.element!.scroll(0, this.phaseAxis.currentScroll);
            }, -1);
        }
    }

    private setRange(viewMode: ViewMode, selectedDate: string | null) {
        this.version++;
        const scheduler: any = this.$refs.scheduler;
        if (scheduler && typeof (scheduler.getWindow && scheduler.getWindow) === 'function') {
            let start: Date = scheduler.getWindow && scheduler.getWindow().start;
            let windowEnd: Date = scheduler.getWindow && scheduler.getWindow().end;
            const diffDay = differenceInCalendarDays(windowEnd, start);

            // Lorsque l'on change les années, on garde la même date.
            if (selectedDate) {
                start = new Date(selectedDate);
                this.oldCurentYear = start.getFullYear();
                this.currentYear = start.getFullYear();
                windowEnd = addDays(start, diffDay);
                this.selectedDate = null;
            } else if (this.oldCurentYear && this.oldCurentYear !== this.currentYear) {
                start = new Date(this.currentYear, start.getMonth(), start.getDate());
                windowEnd = addDays(start, diffDay);
                this.oldCurentYear = this.currentYear;
            } else if (Math.random() === 2) {
                // Fonctionnalité désactivée
                switch (viewMode) {
                    case ViewMode.Year: {
                        start = new Date(this.currentYear, start.getMonth(), start.getDate());
                        windowEnd = addYears(start, 1);
                        break;
                    }
                    case ViewMode.Month: {
                        start = new Date(
                            start.getMonth() < 11 /* decembre */ ? this.currentYear : this.currentYear + 1,
                            start.getMonth(),
                            1
                        );
                        windowEnd = addMonths(start, 1);
                        break;
                    }
                    case ViewMode.Week: {
                        let currentStart = start;
                        for (var i = 0; i < 7; i++) {
                            if (addDays(currentStart, i).getDay() === 1 /* lundi */) {
                                currentStart = addDays(currentStart, i);
                            }
                        }
                        start = new Date(this.currentYear, currentStart.getMonth(), currentStart.getDate());
                        windowEnd = addDays(start, 7);
                        break;
                    }
                }
            }
            return { start: start, end: windowEnd };
        }
        return { start: new Date(this.currentYear, 0, 1), end: new Date(this.currentYear, 2, 1) };
    }
}
